import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './AccountMenu.css';

const AccountMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      setIsOpen(false);
      await logout();
      // Force navigation to login and replace history to prevent back navigation issues
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, navigate to login to prevent stuck state
      navigate('/login', { replace: true });
    }
  };

  const handleDashboardNavigation = () => {
    setIsOpen(false);
    switch (user?.role) {
      case 'corporate':
        navigate('/corporate');
        break;
      case 'regional':
        navigate('/regional');
        break;
      case 'district':
        navigate('/district');
        break;
      case 'dealer':
        navigate('/dashboard');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Get display name
  const getDisplayName = () => {
    if (user?.name) return user.name;
    if (user?.manager_name) return user.manager_name;
    if (user?.username) return user.username;
    return 'User';
  };

  // Get role display text
  const getRoleDisplay = () => {
    switch (user?.role) {
      case 'dealer':
        return 'Dealer';
      case 'district':
        return 'District Manager';
      case 'regional':
        return 'Regional Manager';
      case 'corporate':
        return 'Corporate';
      default:
        return 'User';
    }
  };

  // Get dashboard label
  const getDashboardLabel = () => {
    switch (user?.role) {
      case 'corporate':
        return 'Corporate Dashboard';
      case 'regional':
        return 'Regional Dashboard';
      case 'district':
        return 'District Dashboard';
      case 'dealer':
        return 'Dealer Dashboard';
      default:
        return 'Dashboard';
    }
  };

  return (
    <div className="account-menu" ref={menuRef}>
      <button className="account-menu-trigger" onClick={toggleMenu} aria-label="Account menu">
        <svg className="account-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
          <path d="M12 14C7.03125 14 3 18.0312 3 23H21C21 18.0312 16.9688 14 12 14Z" fill="currentColor"/>
        </svg>
        <span className="account-name">{getDisplayName()}</span>
        <svg className={`dropdown-arrow ${isOpen ? 'open' : ''}`} width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {isOpen && (
        <div className="account-menu-dropdown">
          <div className="account-info">
            <div className="account-avatar">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="currentColor"/>
                <path d="M12 14C7.03125 14 3 18.0312 3 23H21C21 18.0312 16.9688 14 12 14Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="account-details">
              <div className="account-name-full">{getDisplayName()}</div>
              <div className="account-role">{getRoleDisplay()}</div>
              {user?.email && <div className="account-email">{user.email}</div>}
              {user?.distributor && user.distributor !== 'All' && (
                <div className="account-distributor">{user.distributor}</div>
              )}
            </div>
          </div>
          <div className="account-menu-divider"></div>
          <button className="account-menu-item dashboard-item" onClick={handleDashboardNavigation}>
            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 13H11V3H3V13ZM3 21H11V15H3V21ZM13 21H21V11H13V21ZM13 3V9H21V3H13Z" fill="currentColor"/>
            </svg>
            <span>{getDashboardLabel()}</span>
          </button>
          <button className="account-menu-item logout-item" onClick={handleLogout}>
            <svg className="menu-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Logout</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
